import type {
  ReportSectionDto,
  UpdateReportSectionRequestDto,
} from '@act/shared/data-transfer-objects';
import { isCsmAuthoredSection } from '@act/shared/data-transfer-objects';
import { useEffect, useRef } from 'react';

const DATA_SNAPSHOT_PERSIST_DEBOUNCE_MS = 500;

/**
 * Debounced persist for CSM-authored section `dataSnapshot` (Member Spotlight).
 * @param persistSection - PATCH a section row.
 * @param updateSectionLocal - Optimistic local section update.
 * @returns Change and blur handlers for hosted snapshot editors.
 */
export const useReportBuilderDataSnapshot = (
  persistSection: (
    sectionRowId: string,
    body: UpdateReportSectionRequestDto,
  ) => void,
  updateSectionLocal: (
    sectionRowId: string,
    patch: Partial<ReportSectionDto>,
  ) => void,
) => {
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pendingRef = useRef<Record<string, Record<string, unknown>>>({});

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer));
    };
  }, []);

  const handleDataSnapshotChange = (
    section: ReportSectionDto,
    nextSnapshot: Record<string, unknown>,
  ) => {
    if (!isCsmAuthoredSection(section.sectionId)) {
      return;
    }
    pendingRef.current[section.id] = nextSnapshot;
    updateSectionLocal(section.id, { dataSnapshot: nextSnapshot });

    const key = `${section.id}:dataSnapshot`;
    const pending = timersRef.current[key];
    if (pending) {
      clearTimeout(pending);
    }
    timersRef.current[key] = setTimeout(() => {
      const dataSnapshot = pendingRef.current[section.id];
      if (dataSnapshot) {
        persistSection(section.id, { dataSnapshot });
        delete pendingRef.current[section.id];
      }
      delete timersRef.current[key];
    }, DATA_SNAPSHOT_PERSIST_DEBOUNCE_MS);
  };

  const handleDataSnapshotBlur = (
    section: ReportSectionDto,
    nextSnapshot: Record<string, unknown>,
  ) => {
    if (!isCsmAuthoredSection(section.sectionId)) {
      return;
    }
    const key = `${section.id}:dataSnapshot`;
    const pending = timersRef.current[key];
    if (pending) {
      clearTimeout(pending);
      delete timersRef.current[key];
    }
    delete pendingRef.current[section.id];
    updateSectionLocal(section.id, { dataSnapshot: nextSnapshot });
    persistSection(section.id, { dataSnapshot: nextSnapshot });
  };

  return {
    handleDataSnapshotChange,
    handleDataSnapshotBlur,
  };
};
